import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { DateInput } from '@/components/forms/DateInput'
import { normalizeUomLabel } from '@/lib/uom'
import { formatDate, formatDateFull } from '@/lib/dateFormat'
import { PoStatusBadge } from '@/features/proto/ProtoBadges'
import {
  computePoListKpis,
  formatReceivePct,
  PO_OPEN_TOTAL_LABEL,
  PO_TOTAL_LABEL,
  poHasUnknownVat,
  poReceiveProgress,
  poTotal,
  poTotalIncludingKnownVat,
} from '@/features/proto/poListMetrics'
import {
  listPurchaseOrders,
  listSuggestions,
  getPurchaseOrder,
  submitPurchaseOrder,
  approvePurchaseOrder,
  sendPurchaseOrder,
  cancelPurchaseOrder,
  receivePurchaseOrder,
  recordLineShort,
  archivePurchaseOrder,
  unarchivePurchaseOrder,
  listPurchaseRequests,
  submitPurchaseRequest,
  approvePurchaseRequest,
  rejectPurchaseRequest,
  convertPurchaseRequest,
  patchPurchaseOrder,
  patchPoLine,
  createPoLine,
  deletePoLine,
  reopenPurchaseOrder,
  getPoHistory,
  patchPurchaseRequest,
  patchPrLine,
  createPrLine,
  deletePrLine,
  getPrHistory,
  poDtoToUi,
  prDtoToUi,
  money,
  type PurchaseOrderDto,
  type PurchaseRequestDto,
  type PurchaseOrder,
  type DocumentChangeLogDto,
  type PoLine,
  type UiPrLine,
  poWorkspaceService,
  ProcurementApiClientError,
} from '@/services/procurement'
import { receiveFreePo } from '@/services/procurement/freePoApi'
import { PoCreatePageNew } from '@/features/purchasing/PoCreatePageNew'
import { PoKanbanView } from '@/features/purchasing/PoKanbanView'
import { PoDeliveryHistoryTable } from '@/features/purchasing/PoDeliveryHistoryTable'
import { PoCloseModal, type RemainingLineItem } from '@/features/purchasing/PoCloseModal'
import { PoQuickReceiveModal } from '@/features/purchasing/PoQuickReceiveModal'
import { PoPeekDrawer } from '@/features/purchasing/PoPeekDrawer'
import {
  isPoArchiveEligible,
  deriveDeliveryStatus,
  computePoRemaining,
  isLineFullyClosed,
  formatVnNumber,
} from '@/features/purchasing/poWorkflow'
import { supplierService, type Supplier } from '@/services/suppliers'
import { materialService, type Material } from '@/services/materials'
import { useCan } from '@/features/auth/AuthContext'
import { Badge } from '@/components/ui/Badge'
import { DocumentHistoryTab } from '@/components/documents/DocumentHistoryTab'
import { ConfirmPostApprovalEditDialog } from '@/components/documents/ConfirmPostApprovalEditDialog'
import { formatDocumentError, type FormattedDocumentError } from '@/utils/documentErrorMessages'
import { Edit2, Plus, Trash2, RotateCcw, X, Archive, ArchiveRestore, Save, GitFork } from 'lucide-react'
import { ExcelEditableTable, type ExcelColumn } from '@/components/table/ExcelEditableTable'
import { FileExplorer } from '@/components/files/FileExplorer'
import { AttachmentGallery } from '@/components/attachments/AttachmentGallery'
import { createPoWorkspaceAttachmentAdapter } from '@/services/attachments/poWorkspaceAttachmentAdapter'
import { PoLineTraceabilityModal } from '@/features/purchasing/PoLineTraceabilityModal'
import {
  getPoLineTraceabilityApi,
  type PoLineTraceabilityResult,
} from '@/services/procurement/materialRequirementApiClient'
import {
  FreeSheetTable,
  type FreeColumn,
  type FreeRow,
} from '@/components/table/FreeSheetTable'
import {
  CORE_COLUMNS,
  normalizeColumns,
  parseNumberVi,
  normalizeFreeLine,
  normalizeServerNumber,
} from '@/components/table/freeSheetModel'
import { FreePoReceiveTab } from '@/features/purchasing/FreePoReceiveTab'
import { updateFreePoContent } from '@/services/procurement/freePoApi'
import { DEFAULT_PO_SIGNERS, PoSignerEditor, type PoSigner } from '@/features/purchasing/PoSignerEditor'
import { PoReceiveContextMenu } from '@/features/purchasing/PoReceiveActions'

export interface PoLineTableRow extends Record<string, unknown> {
  id: string
  materialId: string
  itemCode: string
  itemName: string
  spec: string
  qty: number | string
  uom: string
  priceUom?: string | null
  needsConversion?: boolean
  unitPriceSystem: number | null
  unitPriceActual: number
  vat: number | null
  vatRateStatus: string
  priceSource: string
  priceRuleId?: string | null
  priceRuleLabel?: string | null
  manualReason?: string
  receivedQty: number
  shortQty: number
  shortResolution?: string | null
  requirementLineId?: string | null
  lineTotal: number
  isNew?: boolean
  spec_attributes?: Record<string, unknown> | null
}

function poLineToTableRow(l: PoLine): PoLineTableRow {
  const needsConversion = Boolean(
    l.priceUom &&
    normalizeUomLabel(l.uom) !== normalizeUomLabel(l.priceUom),
  )
  return {
    id: l.id,
    materialId: l.materialId,
    itemCode: l.itemCode,
    itemName: l.itemName,
    spec: l.spec || '',
    spec_attributes: l.spec_attributes ?? null,
    qty: l.qty,
    uom: l.uom,
    priceUom: l.priceUom ?? null,
    needsConversion,
    unitPriceSystem: l.unitPriceSystem,
    unitPriceActual: l.unitPriceActual,
    vat: l.vat,
    vatRateStatus: l.vatRateStatus,
    priceSource: l.priceSource,
    priceRuleId: l.priceRuleId ?? null,
    priceRuleLabel: l.priceRuleLabel ?? null,
    manualReason: l.manualReason || '',
    receivedQty: l.receivedQty,
    shortQty: l.shortQty,
    shortResolution: l.shortResolution ?? null,
    requirementLineId: l.requirementLineId ?? null,
    lineTotal: l.lineTotal,
    isNew: false,
  }
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function defaultDeliveryDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return d.toISOString().slice(0, 10)
}

// ─── PR EDIT MODALS ──────────────────────────────────────────────

interface PrHeaderEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    note?: string | null
    reason?: string
  }) => void
  initialData: {
    note?: string | null
  }
  loading?: boolean
}

function PrHeaderEditModal({
  isOpen,
  onClose,
  onSave,
  initialData,
  loading = false,
}: PrHeaderEditModalProps) {
  const [note, setNote] = useState(initialData.note ?? '')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen) {
      setNote(initialData.note ?? '')
      setReason('')
    }
  }, [isOpen, initialData])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa thông tin PR</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          <div>
            <label className="block font-medium text-slate-700 mb-1">Ghi chú PR</label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú yêu cầu mua hàng..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Lý do chỉnh sửa</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: Bổ sung thông tin yêu cầu..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={loading}
              onClick={() =>
                onSave({
                  note: note || null,
                  reason: reason || 'Chỉnh sửa thông tin PR',
                })
              }
            >
              Lưu thay đổi
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PrLineEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    material_id?: string
    qty: number
    uom: string
    note?: string | null
    reason?: string
  }) => void
  line: UiPrLine | null
  materials: Material[]
  loading?: boolean
}

function PrLineEditModal({
  isOpen,
  onClose,
  onSave,
  line,
  materials,
  loading = false,
}: PrLineEditModalProps) {
  const [materialId, setMaterialId] = useState('')
  const [qty, setQty] = useState<number>(1)
  const [uom, setUom] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen && line) {
      setMaterialId(line.materialId)
      setQty(line.qty)
      setUom(line.uom)
      setNote(line.note ?? '')
      setReason('')
    }
  }, [isOpen, line])

  if (!isOpen || !line) return null

  const isInvalid = !materialId || qty <= 0 || !uom

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa dòng PR: {line.materialCode || line.materialId}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          <div>
            <label className="block font-medium text-slate-700 mb-1">Chọn vật tư</label>
            <select
              value={materialId}
              onChange={(e) => {
                setMaterialId(e.target.value)
                const m = materials.find((mat) => mat.id === e.target.value)
                if (m?.uom_default) setUom(m.uom_default)
              }}
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            >
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} - {m.name} ({m.uom_default})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Số lượng yêu cầu <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0.0001"
                step="any"
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Đơn vị tính <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={uom}
                onChange={(e) => setUom(e.target.value)}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Ghi chú dòng</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú chi tiết..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Lý do chỉnh sửa</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: Thay đổi định mức sản xuất..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isInvalid || loading}
              onClick={() =>
                onSave({
                  material_id: materialId,
                  qty,
                  uom,
                  note: note || null,
                  reason: reason || 'Chỉnh sửa dòng PR',
                })
              }
            >
              Lưu dòng
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PrLineAddModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    material_id: string
    qty: number
    uom: string
    note?: string | null
    reason?: string
  }) => void
  materials: Material[]
  loading?: boolean
}

function PrLineAddModal({
  isOpen,
  onClose,
  onSave,
  materials,
  loading = false,
}: PrLineAddModalProps) {
  const [materialId, setMaterialId] = useState('')
  const [qty, setQty] = useState<number>(1)
  const [uom, setUom] = useState('')
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen) {
      const first = materials[0]
      setMaterialId(first?.id ?? '')
      setQty(1)
      setUom(first?.uom_default ?? 'M3')
      setNote('')
      setReason('')
    }
  }, [isOpen, materials])

  if (!isOpen) return null

  const isInvalid = !materialId || qty <= 0 || !uom

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Plus className="h-5 w-5 text-emerald-700" />
            <span>Thêm dòng hàng vào PR</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Chọn vật tư <span className="text-red-500">*</span>
            </label>
            <select
              value={materialId}
              onChange={(e) => {
                setMaterialId(e.target.value)
                const m = materials.find((mat) => mat.id === e.target.value)
                if (m?.uom_default) setUom(m.uom_default)
              }}
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            >
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} - {m.name} ({m.uom_default})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Số lượng yêu cầu <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0.0001"
                step="any"
                value={qty}
                onChange={(e) => setQty(Number(e.target.value))}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Đơn vị tính <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={uom}
                onChange={(e) => setUom(e.target.value)}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
              />
            </div>
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Ghi chú dòng</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú chi tiết..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">Lý do thêm dòng</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="VD: Bổ sung theo yêu cầu xưởng..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isInvalid || loading}
              onClick={() =>
                onSave({
                  material_id: materialId,
                  qty,
                  uom,
                  note: note || null,
                  reason: reason || 'Thêm dòng PR',
                })
              }
            >
              Thêm dòng
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── PR LIST PAGE ────────────────────────────────────────────────

export function PrListPage() {
  const navigate = useNavigate()
  const canWritePr = useCan('pr.write')
  const [prs, setPrs] = useState<PurchaseRequestDto[]>([])
  const [materials, setMaterials] = useState<Material[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formattedError, setFormattedError] = useState<FormattedDocumentError | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [convertDeliveryDate, setConvertDeliveryDate] = useState(defaultDeliveryDate())
  const [showConvertModal, setShowConvertModal] = useState(false)

  const [prTab, setPrTab] = useState<'lines' | 'history'>('lines')
  const [prHistoryLogs, setPrHistoryLogs] = useState<DocumentChangeLogDto[]>([])
  const [prHistoryLoading, setPrHistoryLoading] = useState(false)
  const [prHistoryError, setPrHistoryError] = useState<string | null>(null)

  const [headerEditOpen, setHeaderEditOpen] = useState(false)
  const [lineEditOpen, setLineEditOpen] = useState(false)
  const [lineToEdit, setLineToEdit] = useState<UiPrLine | null>(null)
  const [lineAddOpen, setLineAddOpen] = useState(false)

  async function loadData() {
    setLoading(true)
    setError(null)
    setFormattedError(null)
    try {
      const [list, mats] = await Promise.all([
        listPurchaseRequests(),
        materialService.list(),
      ])
      setPrs(list)
      setMaterials(mats)
      if (list.length > 0 && (!selectedId || !list.some((p) => p.id === selectedId))) {
        setSelectedId(list[0].id)
      }
    } catch (err) {
      const formatted = formatDocumentError(err)
      setFormattedError(formatted)
      setError(formatted.message)
    } finally {
      setLoading(false)
    }
  }

  async function loadHistory(prId: string) {
    if (!prId) return
    setPrHistoryLoading(true)
    setPrHistoryError(null)
    try {
      const logs = await getPrHistory(prId)
      setPrHistoryLogs(logs)
    } catch (err: any) {
      setPrHistoryError(err instanceof Error ? err.message : 'Không tải được lịch sử PR')
    } finally {
      setPrHistoryLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [])

  useEffect(() => {
    if (prTab === 'history' && selectedId) {
      void loadHistory(selectedId)
    }
  }, [prTab, selectedId])

  const selectedDto = useMemo(() => {
    return prs.find((p) => p.id === selectedId) ?? prs[0] ?? null
  }, [prs, selectedId])

  const selected = useMemo(() => {
    return selectedDto ? prDtoToUi(selectedDto) : null
  }, [selectedDto])

  const prTone = (status: string) => {
    if (status === 'APPROVED') return 'success' as const
    if (status === 'REJECTED') return 'danger' as const
    if (status === 'SUBMITTED') return 'info' as const
    if (status === 'CONVERTED') return 'neutral' as const
    return 'neutral' as const
  }

  async function handleAction(
    fn: () => Promise<unknown>,
    successMsg: string,
  ) {
    setError(null)
    setFormattedError(null)
    setActionSuccess(null)
    try {
      await fn()
      setActionSuccess(successMsg)
      await loadData()
      if (prTab === 'history' && selectedId) {
        void loadHistory(selectedId)
      }
    } catch (err: any) {
      if (err instanceof ProcurementApiClientError) {
        if (err.code === 'DELIVERY_DATE_REQUIRED') {
          setError('Ngày giao hàng là bắt buộc khi chuyển PR thành PO')
        } else if (err.code === 'BASE_UOM_MISSING') {
          setError(`Lỗi: ${err.message}`)
        } else {
          const formatted = formatDocumentError(err)
          setFormattedError(formatted)
          setError(formatted.message)
        }
      } else {
        const formatted = formatDocumentError(err)
        setFormattedError(formatted)
        setError(formatted.message)
      }
    }
  }

  async function handleSaveHeader(data: { note?: string | null; reason?: string }) {
    if (!selected) return
    setHeaderEditOpen(false)
    await handleAction(
      () =>
        patchPurchaseRequest(selected.id, {
          note: data.note,
          notes: data.note,
          reason: data.reason || 'Chỉnh sửa thông tin PR',
        }),
      'Đã cập nhật thông tin PR thành công',
    )
  }

  async function handleSaveLine(data: {
    material_id?: string
    qty: number
    uom: string
    note?: string | null
    reason?: string
  }) {
    if (!selected || !lineToEdit?.id) return
    const lineId = lineToEdit.id
    setLineEditOpen(false)
    setLineToEdit(null)
    await handleAction(
      () =>
        patchPrLine(selected.id, lineId, {
          material_id: data.material_id,
          qty: data.qty,
          uom: data.uom,
          note: data.note,
          notes: data.note,
          reason: data.reason || 'Chỉnh sửa dòng PR',
        }),
      'Đã cập nhật dòng PR thành công',
    )
  }

  async function handleSaveNewLine(data: {
    material_id: string
    qty: number
    uom: string
    note?: string | null
    reason?: string
  }) {
    if (!selected) return
    setLineAddOpen(false)
    await handleAction(
      () =>
        createPrLine(selected.id, {
          material_id: data.material_id,
          qty: data.qty,
          uom: data.uom,
          note: data.note,
          notes: data.note,
          reason: data.reason || 'Thêm dòng PR',
        }),
      'Đã thêm dòng vào PR thành công',
    )
  }

  async function handleDeleteLine(line: UiPrLine) {
    if (!selected || !line.id) return
    const lineLabel = line.materialCode || line.materialId
    const reason = window.prompt(`Xác nhận xóa dòng ${lineLabel}? Nhập lý do (tùy chọn):`)
    if (reason === null) return
    await handleAction(
      () => deletePrLine(selected.id, line.id!, reason || 'Xóa dòng PR'),
      `Đã xóa dòng ${lineLabel}`,
    )
  }

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Yêu cầu mua hàng' },
        ]}
      />
      <PageHeader
        title="Yêu cầu mua hàng (PR)"
        subtitle="Từ đề xuất mua → PR → PO · Kết nối Postgres SoT"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => navigate('/purchasing/po/new-from-bom')}
            >
              Tạo PO từ BOM
            </Button>
            <Button onClick={() => navigate('/purchasing/po/new')}>Tạo PO</Button>
          </div>
        }
      />

      {formattedError ? (
        <div className="mb-3 rounded-[var(--radius-md)] border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          <div className="font-semibold mb-0.5">Lỗi ({formattedError.code}):</div>
          <div>{formattedError.message}</div>
          {formattedError.link && (
            <div className="mt-2">
              <a
                href={formattedError.link.url}
                className="font-semibold text-blue-600 underline hover:text-blue-800"
              >
                {formattedError.link.label} →
              </a>
            </div>
          )}
        </div>
      ) : error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {actionSuccess}
        </div>
      ) : null}

      <div className="grid gap-3 xl:grid-cols-[minmax(240px,320px)_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
          <div className="border-b border-[var(--color-border)] bg-slate-50 px-3 py-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
            Danh sách PR ({prs.length})
          </div>
          <ul className="max-h-[calc(100vh-260px)] divide-y divide-slate-100 overflow-y-auto">
            {loading ? (
              <li className="px-3 py-8 text-center text-sm text-slate-400">Đang tải...</li>
            ) : prs.length === 0 ? (
              <li className="px-3 py-8 text-center text-sm text-slate-400">
                Chưa có PR nào
              </li>
            ) : (
              prs.map((pr) => {
                const active = selected?.id === pr.id
                return (
                  <li key={pr.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(pr.id)}
                      className={`flex w-full flex-col gap-1 px-3 py-2.5 text-left transition ${
                        active
                          ? 'bg-[var(--color-accent-soft)]'
                          : 'hover:bg-[var(--color-bg)]'
                      }`}
                    >
                      <span className="text-sm font-semibold">{pr.code}</span>
                      <span className="flex items-center justify-between gap-2">
                        <StatusBadge
                          tone={prTone(pr.status)}
                          label={pr.status}
                          showDot={false}
                        />
                        <span className="text-xs text-slate-500" title={formatDateFull(pr.created_at)}>
                          {formatDate(pr.created_at)} · {pr.lines?.length ?? 0} dòng
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>

        <div className="min-w-0 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          {!selected ? (
            <p className="text-sm text-slate-500">Chọn một PR ở danh sách.</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h2 className="text-lg font-semibold text-[var(--color-text)]">
                    {selected.code}
                  </h2>
                  <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                    Ngày {selected.createdAt}
                    {selected.department ? ` · Phòng ban: ${selected.department}` : ''}
                    {selected.requestedBy ? ` · Người yêu cầu: ${selected.requestedBy}` : ''}
                  </p>
                  {(selectedDto?.notes || (selectedDto as any)?.note) && (
                    <p className="mt-1 text-xs text-slate-600 italic">
                      Ghi chú: {selectedDto?.notes || (selectedDto as any)?.note}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge
                    tone={prTone(selected.status)}
                    label={selected.status}
                  />
                  {canWritePr && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setHeaderEditOpen(true)}
                    >
                      <Edit2 className="mr-1 h-3.5 w-3.5" />
                      Sửa PR
                    </Button>
                  )}
                </div>
              </div>

              {selected.status === 'CONVERTED' && (
                <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <span className="font-semibold">Lưu ý:</span> PR này đã được chuyển thành PO. Việc chỉnh sửa PR sẽ không làm thay đổi các PO đã tạo trước đó.
                </div>
              )}

              <div className="mb-3 flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5 w-fit">
                <button
                  type="button"
                  className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                    prTab === 'lines'
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                  }`}
                  onClick={() => setPrTab('lines')}
                >
                  Dòng hàng
                </button>
                <button
                  type="button"
                  className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                    prTab === 'history'
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                  }`}
                  onClick={() => setPrTab('history')}
                >
                  Lịch sử
                </button>
              </div>

              {prTab === 'lines' && (
                <div className="mb-3 overflow-hidden rounded-[var(--radius-sm)] border border-[var(--color-border)]">
                  {canWritePr && (
                    <div className="flex justify-end p-2 border-b border-slate-100 bg-slate-50/50">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setLineAddOpen(true)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Thêm dòng hàng
                      </Button>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="min-w-[520px] w-full text-left text-sm">
                      <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                        <tr>
                          <th className="px-3 py-2">Vật tư</th>
                          <th className="px-3 py-2 text-right">SL</th>
                          <th className="px-3 py-2">ĐVT</th>
                          <th className="px-3 py-2">Ghi chú</th>
                          {canWritePr && (
                            <th className="px-3 py-2 text-right">Thao tác</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {selected.lines.map((line, idx) => (
                          <tr
                            key={`${selected.id}-${line.id || idx}`}
                            className="border-t border-slate-100"
                          >
                            <td className="px-3 py-2">
                              <div className="font-mono text-xs font-semibold">
                                {line.materialCode ?? line.materialId}
                              </div>
                              <div className="text-xs text-slate-600">
                                {line.materialName ?? '—'}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {line.qty.toLocaleString('vi-VN')}
                            </td>
                            <td className="px-3 py-2">{line.uom}</td>
                            <td className="px-3 py-2 text-xs text-slate-600">
                              {line.note || '—'}
                            </td>
                            {canWritePr && (
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setLineToEdit(line)
                                      setLineEditOpen(true)
                                    }}
                                    className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                    title="Chỉnh sửa dòng"
                                  >
                                    <Edit2 className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleDeleteLine(line)}
                                    className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                                    title="Xóa dòng"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {prTab === 'history' && (
                <div className="mb-3 rounded-[var(--radius-sm)] border border-[var(--color-border)] p-3">
                  <DocumentHistoryTab
                    logs={prHistoryLogs}
                    loading={prHistoryLoading}
                    error={prHistoryError}
                    emptyMessage="Chưa có lịch sử thay đổi cho PR này."
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {selected.status === 'DRAFT' && (
                  <Button
                    size="sm"
                    onClick={() =>
                      handleAction(
                        () => submitPurchaseRequest(selected.id),
                        'Đã gửi duyệt PR',
                      )
                    }
                  >
                    Gửi duyệt PR
                  </Button>
                )}
                {selected.status === 'SUBMITTED' && (
                  <>
                    <Button
                      size="sm"
                      onClick={() =>
                        handleAction(
                          () => approvePurchaseRequest(selected.id),
                          'Đã duyệt PR thành công',
                        )
                      }
                    >
                      Duyệt PR
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        const reason = window.prompt('Nhập lý do từ chối:')
                        if (reason !== null) {
                          void handleAction(
                            () => rejectPurchaseRequest(selected.id, reason),
                            'Đã từ chối PR',
                          )
                        }
                      }}
                    >
                      Từ chối PR
                    </Button>
                  </>
                )}
                {selected.status === 'APPROVED' && (
                  <Button
                    size="sm"
                    onClick={() => setShowConvertModal(true)}
                  >
                    Chuyển thành PO
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate('/purchasing/suggestions')}
                >
                  Xem đề xuất mua
                </Button>
              </div>

              {showConvertModal && (
                <div className="mt-4 rounded border border-blue-200 bg-blue-50 p-3 text-sm">
                  <div className="font-semibold text-blue-900 mb-2">
                    Tạo PO từ PR {selected.code}
                  </div>
                  <label className="block mb-2 text-xs text-blue-800">
                    Ngày giao hàng dự kiến:
                    <DateInput
                      className="mt-1 block w-48 rounded border border-blue-300 bg-white px-2 py-1 text-sm text-slate-800"
                      value={convertDeliveryDate}
                      onChange={setConvertDeliveryDate}
                    />
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        setShowConvertModal(false)
                        await handleAction(async () => {
                          const res = await convertPurchaseRequest(selected.id, {
                            delivery_date: convertDeliveryDate,
                          })
                          if (res.purchase_orders?.length) {
                            navigate(`/purchasing/po/${res.purchase_orders[0].id}`)
                          }
                        }, 'Đã chuyển PR thành PO thành công')
                      }}
                    >
                      Xác nhận tạo PO
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setShowConvertModal(false)}
                    >
                      Hủy
                    </Button>
                  </div>
                </div>
              )}

              <PrHeaderEditModal
                isOpen={headerEditOpen}
                onClose={() => setHeaderEditOpen(false)}
                onSave={handleSaveHeader}
                initialData={{
                  note: selectedDto?.notes ?? (selectedDto as any)?.note ?? null,
                }}
              />

              <PrLineEditModal
                isOpen={lineEditOpen}
                onClose={() => {
                  setLineEditOpen(false)
                  setLineToEdit(null)
                }}
                onSave={handleSaveLine}
                line={lineToEdit}
                materials={materials}
              />

              <PrLineAddModal
                isOpen={lineAddOpen}
                onClose={() => setLineAddOpen(false)}
                onSave={handleSaveNewLine}
                materials={materials}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── PO LIST PAGE ────────────────────────────────────────────────

export function PoListPage() {
  const navigate = useNavigate()
  const canWriteReceiving = useCan('receiving.write')
  const canWritePo = useCan('po.write')
  const [pos, setPos] = useState<PurchaseOrderDto[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<'list' | 'kanban' | 'delivery'>('kanban')
  const [showArchived, setShowArchived] = useState(false)
  const [openSuggestionsCount, setOpenSuggestionsCount] = useState<number>(0)
  const [quickReceiveTarget, setQuickReceiveTarget] = useState<{
    po: PurchaseOrder
    targetCol: 'PARTIAL' | 'COMPLETED'
  } | null>(null)
  const [kindFilter, setKindFilter] = useState<'ALL' | 'STANDARD' | 'FREE'>('ALL')
  const [peekPoId, setPeekPoId] = useState<string | null>(null)
  const [receiveMenu, setReceiveMenu] = useState<{ po: PurchaseOrder; x: number; y: number } | null>(null)
  const asOf = todayIso()

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const [poList, supList, sugList] = await Promise.all([
        listPurchaseOrders({ includeArchived: true }),
        supplierService.list(),
        listSuggestions('OPEN').catch(() => []),
      ])
      setPos(poList)
      setSuppliers(supList)
      setOpenSuggestionsCount(sugList.length)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách đơn mua hàng')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [])

  const archivedCount = useMemo(() => pos.filter((p) => Boolean(p.archived_at)).length, [pos])
  const filteredPos = useMemo(() => {
    if (showArchived) return pos
    return pos.filter((p) => !p.archived_at)
  }, [pos, showArchived])

  const uiPos: PurchaseOrder[] = useMemo(() => filteredPos.map(poDtoToUi), [filteredPos])
  const displayPos = useMemo(() => {
    if (kindFilter === 'ALL') return uiPos
    return uiPos.filter((p) => (p.kind || 'STANDARD') === kindFilter)
  }, [uiPos, kindFilter])

  const peekPo = useMemo(
    () => (peekPoId ? displayPos.find((p) => p.id === peekPoId) || null : null),
    [displayPos, peekPoId],
  )
  const kpis = useMemo(() => computePoListKpis(displayPos, asOf), [displayPos, asOf])
  const openPos = useMemo(() => displayPos.filter((p) => p.status !== 'CANCELLED'), [displayPos])
  const totalOpenPoValue = useMemo(
    () => openPos.reduce((sum, p) => sum + poTotalIncludingKnownVat(p), 0),
    [openPos],
  )
  const totalOpenPoHasUnknownVat = useMemo(
    () => openPos.some(poHasUnknownVat),
    [openPos],
  )

  function getSupplierName(supId: string, row?: PurchaseOrder): string {
    const found = suppliers.find((s) => s.id === supId)?.name
    if (found) return found
    return row?.supplierName ?? '—'
  }

  const columns: ProtoColumn<PurchaseOrder>[] = [
    {
      id: 'code',
      header: 'PO',
      sortable: true,
      width: 120,
      cell: (r) => r.code,
      sortValue: (r) => r.code,
    },
    {
      id: 'sup',
      header: 'NCC',
      width: 160,
      cell: (r) => getSupplierName(r.supplierId, r),
      sortValue: (r) => getSupplierName(r.supplierId, r),
    },
    {
      // Tìm kiếm ô trên đầu bảng sẽ khớp theo vật tư trong PO (mã, tên, quy cách).
      id: 'materials',
      header: 'Vật tư',
      width: 260,
      cell: (r) => {
        const names = r.lines.map((l) => l.itemName).filter(Boolean)
        const text = names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ')
        return <span title={names.join(', ')} className="block truncate">{text || '—'}</span>
      },
      sortValue: (r) => r.lines.map((l) => [l.itemCode, l.itemName, l.spec].filter(Boolean).join(' ')).join(' | '),
    },
    {
      id: 'status',
      header: 'Trạng thái',
      cell: (r) => (
        <div className="flex flex-wrap items-center gap-1">
          <PoStatusBadge status={r.status} />
          {r.kind === 'FREE' && (
            <Badge tone="info" className="text-xs font-medium">
              PO tự do
            </Badge>
          )}
          {r.editedAfterApprovalAt && (
            <Badge tone="warning" className="text-xs font-medium">
              Đã sửa sau duyệt
            </Badge>
          )}
        </div>
      ),
      sortValue: (r) => `${r.status} ${r.kind === 'FREE' ? 'PO tự do' : ''}`,
    },
    {
      // Cột riêng để lọc: giá trị 'Đã lưu trữ' / 'Đang dùng'.
      id: 'archived',
      header: 'Lưu trữ',
      width: 110,
      cell: (r) => (r.archivedAt ? (
        <Badge tone="neutral" className="text-xs font-medium">Đã lưu trữ</Badge>
      ) : '—'),
      sortValue: (r) => (r.archivedAt ? 'Đã lưu trữ' : 'Đang dùng'),
    },
    {
      id: 'lines',
      header: 'Dòng',
      align: 'right',
      cell: (r) => r.lines.length,
      sortValue: (r) => r.lines.length,
    },
    {
      id: 'total',
      header: PO_TOTAL_LABEL,
      align: 'right',
      cell: (r) => money(poTotal(r)),
      sortValue: (r) => poTotal(r),
    },
    {
      id: 'recv',
      header: 'Tiến độ nhận',
      align: 'right',
      cell: (r) => formatReceivePct(poReceiveProgress(r)),
      sortValue: (r) => poReceiveProgress(r),
    },
    {
      id: 'deliv',
      header: 'Giao hàng',
      cell: (r) => r.deliveryDate || '—',
      sortValue: (r) => r.deliveryDate,
    },
    {
      id: 'ver',
      header: 'Ver',
      align: 'right',
      cell: (r) => r.version,
      sortValue: (r) => r.version,
    },
  ]

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Đơn mua hàng' },
        ]}
      />
      <PageHeader
        title="Đơn mua hàng"
        subtitle="DRAFT → … → RECEIVED · Postgres SoT · Snapshot giá"
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/warehouse/returns"
              className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--color-text-main)] hover:bg-slate-50 transition-colors"
            >
              Xem trả hàng NCC →
            </Link>
            <Button
              variant="secondary"
              onClick={() => navigate('/purchasing/po/new-from-bom')}
            >
              Tạo PO từ BOM
            </Button>
            <Button onClick={() => navigate('/purchasing/po/new')}>+ PO mới</Button>
          </div>
        }
      />

      {error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        <Link to="/purchasing/suggestions" className="block transition-transform hover:-translate-y-0.5">
          <KpiCard
            label="Đề xuất mua mở"
            value={openSuggestionsCount}
            tone={openSuggestionsCount > 0 ? 'warning' : 'default'}
            hint="Xem nhu cầu mua hàng →"
          />
        </Link>
        <KpiCard label="PO đang mở" value={kpis.openCount} />
        <KpiCard
          label="Chờ duyệt"
          value={kpis.pendingApprovalCount}
          tone={kpis.pendingApprovalCount ? 'warning' : 'default'}
        />
        <KpiCard label="Đang nhận hàng" value={kpis.receivingCount} />
        <KpiCard
          label="Quá hạn giao"
          value={kpis.overdueDeliveryCount}
          tone={kpis.overdueDeliveryCount ? 'danger' : 'default'}
          hint="Theo ngày giao trên PO, chưa đóng"
        />
        <KpiCard
          label="Tổng tiền PO mở"
          value={money(Math.round(totalOpenPoValue))}
          hint={
            totalOpenPoHasUnknownVat
              ? `${PO_OPEN_TOTAL_LABEL}; còn dòng UNKNOWN`
              : PO_OPEN_TOTAL_LABEL
          }
        />
      </div>

      <PageToolbar
        left={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5">
              <button
                type="button"
                className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                  view === 'list'
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => setView('list')}
              >
                Danh sách
              </button>
              <button
                type="button"
                className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                  view === 'kanban'
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => setView('kanban')}
              >
                Kanban
              </button>
              <button
                type="button"
                className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                  view === 'delivery'
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => setView('delivery')}
              >
                Lịch sử nhận hàng
              </button>
            </div>

            <div className="flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5">
              <button
                type="button"
                className={`rounded-[var(--radius-sm)] px-2.5 py-1 text-xs font-semibold ${
                  kindFilter === 'ALL'
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => setKindFilter('ALL')}
              >
                Tất cả loại
              </button>
              <button
                type="button"
                className={`rounded-[var(--radius-sm)] px-2.5 py-1 text-xs font-semibold ${
                  kindFilter === 'STANDARD'
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => setKindFilter('STANDARD')}
              >
                PO chuẩn
              </button>
              <button
                type="button"
                className={`rounded-[var(--radius-sm)] px-2.5 py-1 text-xs font-semibold ${
                  kindFilter === 'FREE'
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => setKindFilter('FREE')}
              >
                PO tự do
              </button>
            </div>
          </div>
        }
        right={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={showArchived ? 'primary' : 'secondary'}
              onClick={() => setShowArchived((prev) => !prev)}
            >
              <Archive className="mr-1.5 h-3.5 w-3.5" />
              {showArchived
                ? 'Ẩn đã lưu trữ'
                : `Hiện đã lưu trữ (${archivedCount})`}
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-400">Đang tải danh sách PO...</div>
      ) : view === 'list' ? (
        <ProtoExcelTable
          columns={columns}
          rows={displayPos}
          rowKey={(r) => r.id}
          onRowClick={(r) => setPeekPoId(r.id)}
          onRowContextMenu={(r, event) => setReceiveMenu({ po: r, x: event.clientX, y: event.clientY })}
          exportName="po"
          stickyLeftColumnIds={['code', 'sup']}
        />
      ) : view === 'kanban' ? (
        <PoKanbanView
          showArchived={showArchived}
          pos={displayPos}
          suppliers={suppliers}
          onSelectPo={(poId) => setPeekPoId(poId)}
          onPoContextMenu={(po, event) => setReceiveMenu({ po, x: event.clientX, y: event.clientY })}
          onTransitionToSent={async (targetPo) => {
            try {
              if (targetPo.status === 'DRAFT') {
                await submitPurchaseOrder(targetPo.id)
                await approvePurchaseOrder(targetPo.id, { reason: 'Kanban kéo sang Đang giao' })
                await sendPurchaseOrder(targetPo.id)
              } else if (targetPo.status === 'PENDING_APPROVAL') {
                await approvePurchaseOrder(targetPo.id, { reason: 'Kanban kéo sang Đang giao' })
                await sendPurchaseOrder(targetPo.id)
              } else if (targetPo.status === 'APPROVED') {
                await sendPurchaseOrder(targetPo.id)
              }
            } finally {
              await loadData()
            }
          }}
          onTransitionPo={async (targetPo, targetCol) => {
            if (targetCol === 'SENT') {
              if (targetPo.status === 'DRAFT') {
                try {
                  await submitPurchaseOrder(targetPo.id)
                  await approvePurchaseOrder(targetPo.id, { reason: 'Kanban kéo sang Đang giao' })
                  await sendPurchaseOrder(targetPo.id)
                } finally {
                  await loadData()
                }
              } else if (targetPo.status === 'PENDING_APPROVAL') {
                try {
                  await approvePurchaseOrder(targetPo.id, { reason: 'Kanban kéo sang Đang giao' })
                  await sendPurchaseOrder(targetPo.id)
                } finally {
                  await loadData()
                }
              } else if (targetPo.status === 'APPROVED') {
                try {
                  await sendPurchaseOrder(targetPo.id)
                } finally {
                  await loadData()
                }
              } else if (
                targetPo.status === 'PARTIAL' ||
                targetPo.status === 'RECEIVED' ||
                targetPo.status === 'SHORT'
              ) {
                throw new Error(
                  'Đơn mua hàng đã có đợt nhận hàng hoặc đã hoàn thành, không thể chuyển ngược về "Đang giao" qua kéo-thả.',
                )
              } else if (targetPo.status === 'CANCELLED') {
                throw new Error('Không thể chuyển đơn mua hàng đã hủy sang "Đang giao".')
              }
            } else if (targetCol === 'COMPLETED' || targetCol === 'PARTIAL') {
              if (targetPo.status === 'RECEIVED') {
                throw new Error(
                  targetCol === 'PARTIAL'
                    ? 'Đơn mua hàng đã nhận đủ 100%, không còn vật tư nào để nhận thêm sang "Nhận một phần".'
                    : 'Đơn mua hàng đã ở trạng thái nhận đủ hoàn thành.',
                )
              }
              if (targetPo.status === 'CANCELLED') {
                throw new Error('Không thể nhận hàng cho đơn mua hàng đã hủy.')
              }
              if (targetPo.status === 'SHORT') {
                const allClosed =
                  (targetPo.lines || []).length > 0 &&
                  (targetPo.lines || []).every(isLineFullyClosed)
                if (allClosed) {
                  throw new Error(
                    'Đơn mua hàng thiếu đã được đóng hoàn tất, không còn dòng hàng chờ nhận.',
                  )
                }
              }

              const remainingLines = computePoRemaining(targetPo.lines || [])
              if (remainingLines.length === 0) {
                throw new Error('Đơn mua hàng không còn dòng vật tư nào cần nhận thêm.')
              }

              setQuickReceiveTarget({ po: targetPo, targetCol })
            } else if (targetCol === 'DRAFT_GROUP') {
              if (targetPo.status === 'CANCELLED') {
                try {
                  await reopenPurchaseOrder(targetPo.id, 'Kanban khôi phục về Nháp')
                } finally {
                  await loadData()
                }
              } else if (targetPo.status === 'SENT') {
                throw new Error(
                  'Không hỗ trợ chuyển PO đã gửi NCC về Nháp qua kéo-thả — vui lòng vào chi tiết PO để xử lý.',
                )
              } else if (
                targetPo.status === 'PARTIAL' ||
                targetPo.status === 'RECEIVED' ||
                targetPo.status === 'SHORT'
              ) {
                throw new Error(
                  'Không hỗ trợ chuyển PO đã nhận hàng/hoàn thành về Nháp qua kéo-thả — vui lòng vào chi tiết PO để xử lý.',
                )
              } else {
                throw new Error('Không thể chuyển đơn mua hàng về cột Nháp qua kéo-thả.')
              }
            }
          }}
        />
      ) : (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <PoDeliveryHistoryTable
            onPoClick={(targetPoId) => setPeekPoId(targetPoId)}
          />
        </div>
      )}

      <PoQuickReceiveModal
        isOpen={Boolean(quickReceiveTarget)}
        onClose={() => setQuickReceiveTarget(null)}
        poCode={quickReceiveTarget?.po.code ?? ''}
        poStatus={quickReceiveTarget?.po.status ?? 'DRAFT'}
        poKind={quickReceiveTarget?.po.kind ?? 'STANDARD'}
        targetCol={quickReceiveTarget?.targetCol ?? 'COMPLETED'}
        lines={quickReceiveTarget ? computePoRemaining(quickReceiveTarget.po.lines || []) : []}
        onConfirm={async (receivedLines, note) => {
          if (!quickReceiveTarget) return
          const targetPo = quickReceiveTarget.po
          const targetCol = quickReceiveTarget.targetCol

          if (
            targetPo.status === 'DRAFT' ||
            targetPo.status === 'PENDING_APPROVAL' ||
            targetPo.status === 'APPROVED'
          ) {
            if (targetPo.status === 'DRAFT') await submitPurchaseOrder(targetPo.id)
            if (targetPo.status !== 'APPROVED') {
              await approvePurchaseOrder(targetPo.id, {
                reason:
                  targetCol === 'COMPLETED'
                    ? 'Kanban kéo sang Hoàn thành (tự động duyệt & gửi)'
                    : 'Kanban kéo sang Nhận 1 phần (tự động duyệt & gửi)',
              })
            }
            await sendPurchaseOrder(targetPo.id)
          }

          await (targetPo.kind === 'FREE'
            ? receiveFreePo(targetPo.id, {
                lines: receivedLines.map((line) => ({
                  po_free_line_id: line.po_free_line_id ?? line.po_line_id,
                  qty: line.qty,
                })),
                note,
              })
            : receivePurchaseOrder(targetPo.id, {
            lines: receivedLines.map(({ po_line_id, qty }) => ({ po_line_id, qty })),
            note:
              note ||
              (targetCol === 'COMPLETED'
                ? 'Kanban nhận đủ hoàn thành đơn'
                : 'Kanban nhận hàng một phần'),
          }))

          await loadData()
        }}
      />

      <PoReceiveContextMenu
        po={receiveMenu?.po ?? null}
        position={receiveMenu ? { x: receiveMenu.x, y: receiveMenu.y } : null}
        canWrite={canWriteReceiving}
        canWriteAttachments={canWritePo}
        onClose={() => setReceiveMenu(null)}
        onContinue={(po) => setQuickReceiveTarget({ po, targetCol: 'COMPLETED' })}
        onUpdated={loadData}
      />

      <PoPeekDrawer
        open={Boolean(peekPoId && peekPo)}
        onClose={() => setPeekPoId(null)}
        po={peekPo}
        supplierName={peekPo ? getSupplierName(peekPo.supplierId, peekPo) : undefined}
        onOpenDetail={(targetPoId) => {
          setPeekPoId(null)
          navigate(`/purchasing/po/${targetPoId}`)
        }}
      />
    </div>
  )
}

// ─── PO CREATE PAGE ──────────────────────────────────────────────

export function PoCreatePage() {
  return <PoCreatePageNew />
}

// ─── PO EDIT MODALS ──────────────────────────────────────────────

interface PoHeaderEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    supplier_id: string
    delivery_date: string | null
    delivery_site?: string | null
    payment_terms?: string | null
    note?: string | null
    signers: PoSigner[]
  }) => void
  suppliers: Supplier[]
  initialData: {
    supplier_id: string
    delivery_date: string | null
    delivery_site?: string | null
    payment_terms?: string | null
    note?: string | null
    signers: PoSigner[]
  }
  hasReceipts: boolean
  loading?: boolean
}

function PoHeaderEditModal({
  isOpen,
  onClose,
  onSave,
  suppliers,
  initialData,
  hasReceipts,
  loading = false,
}: PoHeaderEditModalProps) {
  const [supplierId, setSupplierId] = useState(initialData.supplier_id)
  const [deliveryDate, setDeliveryDate] = useState(initialData.delivery_date ?? '')
  const [deliverySite, setDeliverySite] = useState(initialData.delivery_site ?? '')
  const [paymentTerms, setPaymentTerms] = useState(initialData.payment_terms ?? '')
  const [note, setNote] = useState(initialData.note ?? '')
  const [signers, setSigners] = useState<PoSigner[]>(initialData.signers)

  useEffect(() => {
    if (isOpen) {
      setSupplierId(initialData.supplier_id)
      setDeliveryDate(initialData.delivery_date ?? '')
      setDeliverySite(initialData.delivery_site ?? '')
      setPaymentTerms(initialData.payment_terms ?? '')
      setNote(initialData.note ?? '')
      setSigners(initialData.signers)
    }
  }, [isOpen, initialData])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa thông tin đơn mua hàng (PO)</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Nhà cung cấp
            </label>
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              disabled={hasReceipts || loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs disabled:bg-slate-100 disabled:text-slate-400"
            >
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} - {s.name}
                </option>
              ))}
            </select>
            {hasReceipts && (
              <p className="mt-1 text-xs text-amber-700">
                Không thể đổi nhà cung cấp vì PO đã có phiếu nhập kho.
              </p>
            )}
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Ngày giao hàng
            </label>
            <DateInput
              value={deliveryDate}
              onChange={setDeliveryDate}
              disabled={loading}
              className="w-full"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Địa chỉ giao hàng
            </label>
            <input
              type="text"
              value={deliverySite}
              onChange={(e) => setDeliverySite(e.target.value)}
              placeholder="Kho chính Mộc Phát..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Điều khoản thanh toán
            </label>
            <input
              type="text"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Thanh toán 30 ngày..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Ghi chú
            </label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ghi chú thêm..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <PoSignerEditor value={signers} editable={!loading} onChange={setSigners} />

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={loading}
              onClick={() => {
                onSave({
                  supplier_id: supplierId,
                  delivery_date: deliveryDate || null,
                  delivery_site: deliverySite || null,
                  payment_terms: paymentTerms || null,
                  note: note || null,
                  signers,
                })
              }}
            >
              Lưu thay đổi
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PoLineEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    order_qty: number
    order_uom?: string
    material_id?: string
    unit_price?: number
    manual_reason?: string
  }) => void
  line: PoLine | null
  materials: Material[]
  loading?: boolean
}

function PoLineEditModal({
  isOpen,
  onClose,
  onSave,
  line,
  materials,
  loading = false,
}: PoLineEditModalProps) {
  const [orderQty, setOrderQty] = useState<number>(0)
  const [materialId, setMaterialId] = useState<string>('')
  const [unitPrice, setUnitPrice] = useState<number>(0)
  const [manualReason, setManualReason] = useState<string>('')
  const [orderUom, setOrderUom] = useState<string>('')

  useEffect(() => {
    if (isOpen && line) {
      setOrderQty(line.qty)
      setMaterialId(line.materialId)
      setUnitPrice(line.unitPriceActual)
      setManualReason(line.manualReason ?? '')
      setOrderUom(line.uom)
    }
  }, [isOpen, line])

  if (!isOpen || !line) return null

  const minQty = line.receivedQty + line.shortQty
  const hasReceived = line.receivedQty > 0 || line.shortQty > 0
  const isQtyInvalid = orderQty < minQty || orderQty <= 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Edit2 className="h-5 w-5 text-emerald-700" />
            <span>Chỉnh sửa dòng hàng: {line.itemCode}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          <div>
            <label className="block font-medium text-slate-700 mb-1">Vật tư</label>
            <select
              value={materialId}
              onChange={(e) => {
                setMaterialId(e.target.value)
                const mat = materials.find((m) => m.id === e.target.value)
                if (mat?.uom_default) setOrderUom(mat.uom_default)
              }}
              disabled={hasReceived || loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs disabled:bg-slate-100 disabled:text-slate-400"
            >
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} - {m.name}
                </option>
              ))}
            </select>
            {hasReceived && (
              <p className="mt-1 text-xs text-amber-700">
                Không thể đổi vật tư của dòng đã nhận/thiếu hàng.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Số lượng đặt <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={minQty}
                step="any"
                value={orderQty}
                onChange={(e) => setOrderQty(Number(e.target.value))}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
              {orderQty < minQty && (
                <p className="mt-1 text-xs text-red-600">
                  Số lượng đặt không thể nhỏ hơn tổng đã nhận và thiếu ({minQty}).
                </p>
              )}
            </div>

            <div>
              <label className="block font-medium text-slate-700 mb-1">Đơn vị đặt</label>
              <input
                type="text"
                value={orderUom}
                onChange={(e) => setOrderUom(e.target.value)}
                disabled={hasReceived || loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs disabled:bg-slate-100 disabled:text-slate-400"
              />
            </div>
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Đơn giá thực tế (VND)
            </label>
            <input
              type="number"
              min="0"
              step="any"
              value={unitPrice}
              onChange={(e) => setUnitPrice(Number(e.target.value))}
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
            />
          </div>

          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Lý do sửa giá / ghi chú
            </label>
            <input
              type="text"
              value={manualReason}
              onChange={(e) => setManualReason(e.target.value)}
              placeholder="VD: Chiết khấu thêm từ NCC..."
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={isQtyInvalid || loading}
              onClick={() => {
                onSave({
                  order_qty: orderQty,
                  order_uom: orderUom,
                  material_id: hasReceived ? undefined : materialId,
                  unit_price: unitPrice,
                  manual_reason: manualReason || undefined,
                })
              }}
            >
              Lưu dòng hàng
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PoLineAddModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: {
    material_id: string
    order_qty: number
    order_uom: string
    manual_price?: number
    manual_reason?: string
    thick?: number
    width?: number
    length?: number
  }) => void
  materials: Material[]
  loading?: boolean
}

function PoLineAddModal({
  isOpen,
  onClose,
  onSave,
  materials,
  loading = false,
}: PoLineAddModalProps) {
  const [materialId, setMaterialId] = useState<string>('')
  const [orderQty, setOrderQty] = useState<number>(1)
  const [orderUom, setOrderUom] = useState<string>('')
  const [manualPrice, setManualPrice] = useState<number | ''>('')
  const [manualReason, setManualReason] = useState<string>('')
  const [thick, setThick] = useState<number | ''>('')
  const [width, setWidth] = useState<number | ''>('')
  const [length, setLength] = useState<number | ''>('')

  useEffect(() => {
    if (isOpen) {
      const firstMat = materials[0]
      setMaterialId(firstMat?.id ?? '')
      setOrderQty(1)
      setOrderUom(firstMat?.uom_default ?? 'M3')
      setManualPrice('')
      setManualReason('')
      setThick('')
      setWidth('')
      setLength('')
    }
  }, [isOpen, materials])

  if (!isOpen) return null

  const isFormValid = materialId && orderQty > 0 && orderUom

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2 font-semibold text-slate-800">
            <Plus className="h-5 w-5 text-emerald-700" />
            <span>Thêm dòng hàng vào PO</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          <div>
            <label className="block font-medium text-slate-700 mb-1">
              Chọn vật tư <span className="text-red-500">*</span>
            </label>
            <select
              value={materialId}
              onChange={(e) => {
                setMaterialId(e.target.value)
                const mat = materials.find((m) => m.id === e.target.value)
                if (mat?.uom_default) setOrderUom(mat.uom_default)
              }}
              disabled={loading}
              className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
            >
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} - {m.name} ({m.uom_default})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Số lượng đặt <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0.0001"
                step="any"
                value={orderQty}
                onChange={(e) => setOrderQty(Number(e.target.value))}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-700 mb-1">
                Đơn vị đặt <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={orderUom}
                onChange={(e) => setOrderUom(e.target.value)}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block font-medium text-slate-700 mb-1">Dày (mm)</label>
              <input
                type="number"
                step="any"
                value={thick}
                onChange={(e) => setThick(e.target.value ? Number(e.target.value) : '')}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>
            <div>
              <label className="block font-medium text-slate-700 mb-1">Rộng (mm)</label>
              <input
                type="number"
                step="any"
                value={width}
                onChange={(e) => setWidth(e.target.value ? Number(e.target.value) : '')}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>
            <div>
              <label className="block font-medium text-slate-700 mb-1">Dài (mm)</label>
              <input
                type="number"
                step="any"
                value={length}
                onChange={(e) => setLength(e.target.value ? Number(e.target.value) : '')}
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block font-medium text-slate-700 mb-1">Đơn giá thủ công (nếu có)</label>
              <input
                type="number"
                min="0"
                step="any"
                value={manualPrice}
                onChange={(e) => setManualPrice(e.target.value ? Number(e.target.value) : '')}
                placeholder="Để trống để áp bảng giá"
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs font-mono"
              />
            </div>
            <div>
              <label className="block font-medium text-slate-700 mb-1">Lý do giá thủ công</label>
              <input
                type="text"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                placeholder="VD: Thỏa thuận riêng..."
                disabled={loading}
                className="w-full rounded border border-[var(--color-border)] p-2 text-xs"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={loading}>
              Hủy
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!isFormValid || loading}
              onClick={() => {
                onSave({
                  material_id: materialId,
                  order_qty: orderQty,
                  order_uom: orderUom,
                  manual_price: manualPrice !== '' ? Number(manualPrice) : undefined,
                  manual_reason: manualReason || undefined,
                  thick: thick !== '' ? Number(thick) : undefined,
                  width: width !== '' ? Number(width) : undefined,
                  length: length !== '' ? Number(length) : undefined,
                })
              }}
            >
              Thêm dòng
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── PO DETAIL PAGE ──────────────────────────────────────────────

export function PoDetailPage() {
  const { poId = '' } = useParams()
  const navigate = useNavigate()
  const canWritePo = useCan('po.write')
  const canWriteReceiving = useCan('receiving.write')
  const [poDto, setPoDto] = useState<PurchaseOrderDto | null>(null)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [materials, setMaterials] = useState<Material[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formattedError, setFormattedError] = useState<FormattedDocumentError | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [recvQty, setRecvQty] = useState<Record<string, string>>({})
  const [shortQty, setShortQty] = useState('100')
  const [tab, setTab] = useState<'lines' | 'receive' | 'delivery' | 'history' | 'files'>('lines')
  const [fileExplorerRefreshKey, setFileExplorerRefreshKey] = useState(0)
  const poAttachmentAdapter = useMemo(() => createPoWorkspaceAttachmentAdapter(poId), [poId])
  const [closeModalOpen, setCloseModalOpen] = useState(false)
  const [closeModalLoading, setCloseModalLoading] = useState(false)

  const [tableLines, setTableLines] = useState<PoLineTableRow[]>([])
  const baselineLinesRef = useRef<PoLineTableRow[] | null>(null)
  const [savingLines, setSavingLines] = useState(false)
  const [isEditingLines, setIsEditingLines] = useState(false)

  const isFreePo = poDto?.kind === 'FREE'
  const [freeColumns, setFreeColumns] = useState<FreeColumn[]>([])
  const [freeRows, setFreeRows] = useState<FreeRow[]>([])
  const [baselineFree, setBaselineFree] = useState<{ columns: FreeColumn[]; rows: FreeRow[] } | null>(null)
  const [savingFreeContent, setSavingFreeContent] = useState(false)
  const [signers, setSigners] = useState<PoSigner[]>([])

  const [headerEditOpen, setHeaderEditOpen] = useState(false)
  const [lineEditOpen, setLineEditOpen] = useState(false)
  const [lineToEdit, setLineToEdit] = useState<PoLine | null>(null)
  const [lineAddOpen, setLineAddOpen] = useState(false)
  const [confirmPostApprovalOpen, setConfirmPostApprovalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<{
    fn: (reason?: string) => Promise<unknown>
    description: string
  } | null>(null)

  const [historyLogs, setHistoryLogs] = useState<DocumentChangeLogDto[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  const [traceModalOpen, setTraceModalOpen] = useState(false)
  const [traceData, setTraceData] = useState<PoLineTraceabilityResult | null>(null)
  const [traceLoading, setTraceLoading] = useState(false)
  const [traceError, setTraceError] = useState<string | null>(null)

  const handleOpenTraceability = useCallback(async (poLineId: string) => {
    setTraceModalOpen(true)
    setTraceLoading(true)
    setTraceError(null)
    setTraceData(null)
    try {
      const res = await getPoLineTraceabilityApi(poLineId)
      setTraceData(res)
    } catch (err: any) {
      console.error('Failed to load PO line traceability:', err)
      setTraceError(err?.message || 'Không thể lấy thông tin truy vết cho dòng hàng này')
    } finally {
      setTraceLoading(false)
    }
  }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    setFormattedError(null)
    try {
      const [po, sups, mats] = await Promise.all([
        getPurchaseOrder(poId),
        supplierService.list(),
        materialService.list(),
      ])
      setPoDto(po)
      setSigners(po?.signers?.length ? po.signers : DEFAULT_PO_SIGNERS)
      if (po?.kind === 'FREE') {
        const rawCols = Array.isArray(po.free_layout) && po.free_layout.length > 0 ? (po.free_layout as FreeColumn[]) : [...CORE_COLUMNS]
        const cols = normalizeColumns(rawCols)
        const rws: FreeRow[] = (po.free_lines || []).map((fl: any) =>
          normalizeFreeLine(fl, cols),
        )
        setFreeColumns(cols)
        setFreeRows(rws)
        setBaselineFree({ columns: cols, rows: rws })
      }
      setSuppliers(sups)
      setMaterials(mats)
    } catch (err) {
      const formatted = formatDocumentError(err)
      setFormattedError(formatted)
      setError(formatted.message)
    } finally {
      setLoading(false)
    }
  }

  async function loadHistory() {
    setHistoryLoading(true)
    setHistoryError(null)
    try {
      const logs = await getPoHistory(poId)
      setHistoryLogs(logs)
    } catch (err: any) {
      setHistoryError(err instanceof Error ? err.message : 'Không tải được lịch sử PO')
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    setIsEditingLines(false)
    void loadData()
  }, [poId])

  useEffect(() => {
    if (tab === 'history' && poId) {
      void loadHistory()
    }
  }, [tab, poId])

  const po = useMemo(() => (poDto ? poDtoToUi(poDto) : null), [poDto])
  const deliveryStatus = useMemo(() => (po ? deriveDeliveryStatus(po) : null), [po])
  const isPostApproval = po ? ['APPROVED', 'SENT', 'PARTIAL', 'RECEIVED'].includes(po.status) : false
  const hasReceipts = po ? po.lines.some((l) => l.receivedQty > 0 || l.shortQty > 0) : false
  const canArchive = useMemo(() => (po ? isPoArchiveEligible(po) : false), [po])
  const isArchived = Boolean(po?.archivedAt)

  const remainingLines: RemainingLineItem[] = useMemo(() => {
    if (!po) return []
    return computePoRemaining(po.lines)
  }, [po])

  const hasRemaining = remainingLines.length > 0

  const computeFreeRow = useCallback((row: FreeRow): FreeRow => {
    const q = typeof row.qty === 'number' ? row.qty : parseNumberVi(row.qty)
    const p = typeof row.unit_price === 'number' ? row.unit_price : parseNumberVi(row.unit_price)
    if (q != null && p != null) {
      return { ...row, line_total: Math.round((q * p + Number.EPSILON) * 100) / 100 }
    }
    return { ...row, line_total: 0 }
  }, [])

  const isFreeDirty = useMemo(() => {
    if (!isFreePo || !baselineFree) return false
    return (
      JSON.stringify(freeColumns) !== JSON.stringify(baselineFree.columns) ||
      JSON.stringify(freeRows) !== JSON.stringify(baselineFree.rows)
    )
  }, [isFreePo, baselineFree, freeColumns, freeRows])

  const handleSaveFreeContent = async () => {
    setSavingFreeContent(true)
    setError(null)
    try {
      const payloadLines = freeRows.map((r) => {
        const extra: Record<string, unknown> = {}
        for (const col of freeColumns) {
          if (!col.core) {
            extra[col.key] = r[col.key] ?? null
          }
        }
        const unitPriceNum =
          r.unit_price != null && r.unit_price !== ''
            ? typeof r.unit_price === 'number'
              ? r.unit_price
              : parseNumberVi(r.unit_price)
            : null
        const qtyNum = typeof r.qty === 'number' ? r.qty : (parseNumberVi(r.qty) ?? 0)
        return {
          id: r.id.startsWith('row_') ? undefined : r.id,
          name: String(r.name ?? '').trim(),
          uom: String(r.uom ?? '').trim(),
          qty: qtyNum,
          unit_price: unitPriceNum != null && unitPriceNum >= 0 ? unitPriceNum : null,
          note: r.note ? String(r.note).trim() : null,
          extra,
        }
      })
      const updated = await updateFreePoContent(poId, {
        free_layout: freeColumns,
        lines: payloadLines,
      })
      setPoDto(updated)
      setBaselineFree({ columns: freeColumns, rows: freeRows })
      setActionSuccess('Đã lưu nội dung PO tự do')
    } catch (err: any) {
      setError(err?.message || 'Không thể lưu nội dung PO tự do')
    } finally {
      setSavingFreeContent(false)
    }
  }

  const handleCancelFreeContent = () => {
    if (baselineFree) {
      setFreeColumns(JSON.parse(JSON.stringify(baselineFree.columns)))
      setFreeRows(JSON.parse(JSON.stringify(baselineFree.rows)))
    }
  }

  const isLinesDirty = useMemo(() => {
    if (!baselineLinesRef.current) return false
    return JSON.stringify(tableLines) !== JSON.stringify(baselineLinesRef.current)
  }, [tableLines])

  useEffect(() => {
    if (po && !isLinesDirty && !isFreePo) {
      const rows = po.lines.map(poLineToTableRow)
      setTableLines(rows)
      baselineLinesRef.current = JSON.parse(JSON.stringify(rows))
    }
  }, [po, isLinesDirty, isFreePo])

  function handleSwitchTab(targetTab: 'lines' | 'receive' | 'delivery' | 'history' | 'files') {
    if (isFreePo && isFreeDirty && targetTab !== 'lines') {
      const ok = window.confirm(
        'Bạn có thay đổi chưa lưu trên bảng PO tự do. Chuyển tab sẽ mất các thay đổi này. Bạn có muốn tiếp tục?',
      )
      if (!ok) return
      handleCancelFreeContent()
    }
    if (!isFreePo && isLinesDirty && targetTab !== 'lines') {
      const ok = window.confirm(
        'Bạn có thay đổi chưa lưu trên bảng dòng PO. Chuyển tab sẽ mất các thay đổi này. Bạn có muốn tiếp tục?',
      )
      if (!ok) return
      handleCancelLinesEdit()
    }
    if (targetTab !== 'lines') {
      setIsEditingLines(false)
    }
    setTab(targetTab)
  }

  useEffect(() => {
    if (!isLinesDirty && !isFreeDirty) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isLinesDirty, isFreeDirty])

  const currentTotal = useMemo(() => {
    if (isFreePo) {
      return freeRows.reduce((sum, r) => sum + (normalizeServerNumber(computeFreeRow(r).line_total) ?? 0), 0)
    }
    if (tableLines.length > 0) {
      return tableLines.reduce((sum, l) => sum + (Number(l.lineTotal) || 0), 0)
    }
    return po ? poTotal(po) : 0
  }, [isFreePo, freeRows, computeFreeRow, tableLines, po])

  function handleLinesTableChange(newRows: PoLineTableRow[]) {
    // I2: Prevent deleting lines that already have receivedQty > 0 || shortQty > 0
    const baseline = baselineLinesRef.current || []
    const newIds = new Set(newRows.map((r) => r.id))
    for (const b of baseline) {
      if (!newIds.has(b.id) && (Number(b.receivedQty) > 0 || Number(b.shortQty) > 0)) {
        alert(
          `Không thể xóa dòng "${b.itemName || b.itemCode}" vì đã có ghi nhận nhận hàng hoặc báo thiếu.`,
        )
        return
      }
    }

    const updated = newRows.map((row) => {
      const q = row.qty
      const p = Math.max(0, Number(row.unitPriceActual) || 0)

      // C3: If material code changed on a new row, automatically resolve material
      if (row.isNew) {
        const codeTrimmed = String(row.itemCode || '').trim().toLowerCase()
        const found = materials.find(
          (m) =>
            m.code.toLowerCase() === codeTrimmed ||
            m.name.toLowerCase() === codeTrimmed,
        )
        if (found && found.id !== row.materialId) {
          row.materialId = found.id
          row.itemCode = found.code
          row.itemName = found.name
          row.uom = found.uom_default || row.uom || 'Cái'
        }
      }

      // C4: Check if conversion is needed between order_uom and price_uom
      const needsConversion = Boolean(
        row.priceUom &&
        normalizeUomLabel(row.uom) !== normalizeUomLabel(row.priceUom),
      )

      let total = Number(row.lineTotal) || 0
      if (!needsConversion) {
        const qtyNum = Math.max(0, Number(q) || 0)
        total = Math.round(qtyNum * p)
      }

      return {
        ...row,
        qty: q,
        unitPriceActual: p,
        lineTotal: total,
        needsConversion,
      }
    })
    setTableLines(updated)
  }

  const emptyRowFactory = useCallback((): PoLineTableRow => {
    const defaultMat = materials[0]
    return {
      id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      materialId: defaultMat?.id || '',
      itemCode: defaultMat?.code || '',
      itemName: defaultMat?.name || '',
      spec: '',
      qty: 1,
      uom: defaultMat?.uom_default || 'Cái',
      priceUom: defaultMat?.uom_default || 'Cái',
      unitPriceSystem: null,
      unitPriceActual: 0,
      vat: 0,
      vatRateStatus: 'UNKNOWN',
      priceSource: 'MANUAL',
      priceRuleId: null,
      priceRuleLabel: null,
      manualReason: '',
      receivedQty: 0,
      shortQty: 0,
      shortResolution: null,
      lineTotal: 0,
      isNew: true,
    }
  }, [materials])

  async function handleSaveLines(reason?: string) {
    if (!po) return
    setSavingLines(true)
    setError(null)
    setFormattedError(null)
    try {
      const baseline = baselineLinesRef.current || []
      const baselineMap = new Map(baseline.map((b) => [b.id, b]))
      const currentIds = new Set(tableLines.map((l) => l.id))

      // I1: Validate quantity > 0 for all rows
      for (let i = 0; i < tableLines.length; i++) {
        const row = tableLines[i]
        const qNum = Number(row.qty)
        if (isNaN(qNum) || qNum <= 0) {
          throw new Error(
            `Dòng ${i + 1} (${row.itemName || row.itemCode || 'Vật tư'}): Số lượng đặt phải lớn hơn 0.`,
          )
        }
      }

      // I2: Check if any deleted row had receivedQty > 0 || shortQty > 0
      for (const b of baseline) {
        if (!currentIds.has(b.id)) {
          if (Number(b.receivedQty) > 0 || Number(b.shortQty) > 0) {
            throw new Error(
              `Không thể xóa dòng "${b.itemName || b.itemCode}" vì đã có ghi nhận nhận hàng hoặc báo thiếu.`,
            )
          }
        }
      }

      // 1. Delete rows removed
      for (const b of baseline) {
        if (!currentIds.has(b.id)) {
          await deletePoLine(po.id, b.id, reason || 'Xóa dòng khỏi PO')
        }
      }

      // 2. Insert new rows or update modified rows
      for (let i = 0; i < tableLines.length; i++) {
        const row = tableLines[i]
        if (row.isNew || row.id.startsWith('new-')) {
          // C3: Ensure material is resolved
          let matId = row.materialId
          if (!matId) {
            const found = materials.find(
              (m) =>
                m.code.toLowerCase() === (row.itemCode || '').trim().toLowerCase() ||
                m.name.toLowerCase() === (row.itemName || '').trim().toLowerCase(),
            )
            if (found) {
              matId = found.id
            } else {
              throw new Error(
                `Dòng mới thứ ${i + 1}: Mã vật tư "${row.itemCode || ''}" không tồn tại trong hệ thống.`,
              )
            }
          }

          const unitPrice = Number(row.unitPriceActual) || 0
          await createPoLine(po.id, {
            material_id: matId,
            order_qty: Number(row.qty),
            order_uom: row.uom || 'Cái',
            spec: row.spec ? row.spec.trim() : null,
            spec_attributes: row.spec_attributes ?? null,
            manual_price: unitPrice > 0 ? unitPrice : undefined,
            manual_reason: row.manualReason?.trim() || (unitPrice > 0 ? 'Thêm dòng mới vào PO' : undefined),
            reason: reason || 'Thêm dòng mới vào PO',
          })
        } else {
          const old = baselineMap.get(row.id)
          if (old) {
            const isPriceChanged = Number(old.unitPriceActual) !== Number(row.unitPriceActual)
            const isChanged =
              Number(old.qty) !== Number(row.qty) ||
              isPriceChanged ||
              (old.manualReason || '') !== (row.manualReason || '') ||
              (old.spec || '') !== (row.spec || '') ||
              old.uom !== row.uom ||
              JSON.stringify(old.spec_attributes ?? null) !== JSON.stringify(row.spec_attributes ?? null)

            if (isChanged) {
              const manualPriceVal = Number(row.unitPriceActual)
              const manualReasonVal =
                row.manualReason?.trim() ||
                (isPriceChanged ? 'Cập nhật đơn giá dòng PO' : undefined)

              // C2: Send manual_price instead of unit_price
              await patchPoLine(po.id, row.id, {
                order_qty: Number(row.qty),
                order_uom: row.uom || undefined,
                manual_price: isPriceChanged ? manualPriceVal : undefined,
                manual_reason: manualReasonVal,
                spec: row.spec !== undefined ? (row.spec ? row.spec.trim() : null) : undefined,
                spec_attributes: row.spec_attributes ?? null,
                reason: reason || 'Chỉnh sửa dòng PO',
              })
            }
          }
        }
      }
      setIsEditingLines(false)
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setFormattedError(formatted)
      setError(formatted.message || 'Lỗi khi lưu các dòng PO')
      // C1: Crucial - rethrow error so handlePoAction knows it failed and does NOT show success banner!
      throw err
    } finally {
      setSavingLines(false)
    }
  }

  function handleSaveLinesClick() {
    const baseline = baselineLinesRef.current || []
    const currentIds = new Set(tableLines.map((l) => l.id))
    const deletedCount = baseline.filter((b) => !currentIds.has(b.id)).length

    if (deletedCount > 0) {
      const ok = window.confirm(
        `Bạn có chắc chắn muốn xóa ${deletedCount} dòng khỏi đơn hàng này?`,
      )
      if (!ok) return
    }

    executeOrConfirmAction(
      (reason) => handleSaveLines(reason),
      'Đã lưu thay đổi các dòng PO thành công',
    )
  }

  function handleCancelLinesEdit() {
    if (baselineLinesRef.current) {
      setTableLines(JSON.parse(JSON.stringify(baselineLinesRef.current)))
    }
    setIsEditingLines(false)
  }

  const lineColumns: ExcelColumn<PoLineTableRow>[] = useMemo(() => {
    const isLocked =
      !isEditingLines || !canWritePo || po?.status === 'CANCELLED' || po?.status === 'RECEIVED'
    return [
      {
        key: 'id',
        label: 'STT',
        width: 50,
        align: 'center',
        readOnly: true,
        sortable: false,
        filterable: false,
        formatter: (_val, _row, index) => String(index + 1),
      },
      {
        key: 'itemCode',
        label: 'Mã VT',
        width: 140,
        sortable: true,
        filterable: true,
        align: 'left',
        readOnly: (row: PoLineTableRow) => !row.isNew || isLocked,
        placeholder: 'Gõ mã/tên VT...',
        renderCell: (val) => {
          return <span className="font-semibold text-slate-800">{String(val || '')}</span>
        },
      },
      {
        key: 'itemName',
        label: 'Tên vật tư',
        width: 180,
        sortable: true,
        filterable: true,
        align: 'left',
        readOnly: true,
      },
      {
        key: 'spec',
        label: 'Quy cách',
        width: 140,
        sortable: true,
        filterable: true,
        align: 'left',
        readOnly: isLocked,
        formatter: (val) => String(val || '—'),
      },
      {
        key: 'qty',
        label: 'SL đặt',
        width: 100,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'number',
        readOnly: isLocked,
        formatter: (val, row) => `${formatVnNumber(Number(val) || 0)} ${row.uom || ''}`,
      },
      {
        key: 'receivedQty',
        label: 'Đã nhận',
        width: 95,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'number',
        readOnly: true,
        formatter: (val, row) => `${formatVnNumber(Number(val) || 0)} ${row.uom || ''}`,
      },
      {
        key: 'shortQty',
        label: 'Short/Hủy',
        width: 95,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'number',
        readOnly: true,
        formatter: (val, row) => `${formatVnNumber(Number(val) || 0)} ${row.uom || ''}`,
      },
      {
        key: 'remainingQty',
        label: 'Còn lại',
        width: 100,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'number',
        readOnly: true,
        renderCell: (_val, row) => {
          const rem = Math.max(0, (Number(row.qty) || 0) - (Number(row.receivedQty) || 0) - (Number(row.shortQty) || 0))
          return (
            <span className="font-semibold text-amber-700">
              {formatVnNumber(rem)} {row.uom}
            </span>
          )
        },
      },
      {
        key: 'unitPriceSystem',
        label: 'Giá HT',
        width: 110,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'currency',
        readOnly: true,
        formatter: (val) => (val !== null && val !== undefined ? money(Number(val)) : '—'),
      },
      {
        key: 'unitPriceActual',
        label: 'Giá TT',
        width: 120,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'currency',
        readOnly: isLocked,
        formatter: (val) => money(Number(val) || 0),
      },
      {
        key: 'vatRateStatus',
        label: 'VAT',
        width: 90,
        sortable: true,
        filterable: true,
        align: 'center',
        readOnly: true,
        renderCell: (val, row) =>
          val === 'UNKNOWN' ? (
            <StatusBadge
              tone="warning"
              label="UNKNOWN"
              showDot={false}
              title="Chưa xác định mã VAT của vật tư/NCC — cần cập nhật trong dữ liệu vật tư"
            />
          ) : (
            <span>{row.vat}%</span>
          ),
      },
      {
        key: 'priceRuleLabel',
        label: 'Rule',
        width: 120,
        sortable: true,
        filterable: true,
        align: 'left',
        readOnly: true,
        formatter: (val) => String(val || '—'),
      },
      {
        key: 'priceSource',
        label: 'Source',
        width: 80,
        sortable: true,
        filterable: true,
        align: 'center',
        readOnly: true,
        formatter: (val) => String(val || 'MANUAL'),
      },
      {
        key: 'manualReason',
        label: 'Lý do giá TT',
        width: 150,
        align: 'left',
        readOnly: isLocked,
        formatter: (val) => String(val || ''),
      },
      {
        key: 'lineTotal',
        label: 'Thành tiền (chưa VAT)',
        width: 170,
        sortable: true,
        filterable: true,
        align: 'right',
        type: 'currency',
        readOnly: true,
        formatter: (val, row) => {
          if (row.needsConversion) {
            return `${money(Number(val) || 0)} (cập nhật sau khi Lưu)`
          }
          return money(Number(val) || 0)
        },
      },
      {
        key: 'requirementLineId',
        label: 'Nguồn BOM',
        width: 90,
        align: 'center',
        readOnly: true,
        sortable: false,
        filterable: false,
        renderCell: (val, row) => {
          if (!val) {
            return <span className="text-slate-300" title="Dòng PO tạo thủ công, không qua BOM">—</span>
          }
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                void handleOpenTraceability(row.id)
              }}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 hover:text-blue-900 transition-colors cursor-pointer"
              title="Xem truy vết nguồn gốc BOM"
            >
              <GitFork className="h-3 w-3" />
              <span>BOM</span>
            </button>
          )
        },
      },
    ]
  }, [isEditingLines, canWritePo, po, materials, tableLines, handleOpenTraceability])

  async function handlePoAction(fn: () => Promise<unknown>, successMsg: string) {
    setError(null)
    setFormattedError(null)
    setActionSuccess(null)
    try {
      await fn()
      setActionSuccess(successMsg)
      await loadData()
      if (tab === 'history') {
        void loadHistory()
      }
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setFormattedError(formatted)
      setError(formatted.message)
    }
  }

  function executeOrConfirmAction(
    fn: (reason?: string) => Promise<unknown>,
    successMsg: string,
  ) {
    if (isPostApproval) {
      setPendingAction({ fn, description: successMsg })
      setConfirmPostApprovalOpen(true)
    } else {
      void handlePoAction(() => fn(), successMsg)
    }
  }

  async function handleConfirmPostApproval(reason: string) {
    if (!pendingAction) return
    const action = pendingAction
    setConfirmPostApprovalOpen(false)
    setPendingAction(null)
    await handlePoAction(() => action.fn(reason), action.description)
  }

  function handleSaveHeader(data: {
    supplier_id: string
    delivery_date: string | null
    delivery_site?: string | null
    payment_terms?: string | null
    note?: string | null
    signers: PoSigner[]
  }) {
    setHeaderEditOpen(false)
    executeOrConfirmAction(
      (reason) =>
        patchPurchaseOrder(poId, {
          supplier_id: data.supplier_id,
          delivery_date: data.delivery_date,
          delivery_site: data.delivery_site,
          note: data.note,
          signers: data.signers,
          reason: reason || 'Chỉnh sửa thông tin đơn hàng',
        }),
      'Đã cập nhật thông tin PO thành công',
    )
  }

  function handleSaveLine(data: {
    order_qty: number
    order_uom?: string
    material_id?: string
    unit_price?: number
    manual_reason?: string
  }) {
    if (!lineToEdit) return
    const lineId = lineToEdit.id
    setLineEditOpen(false)
    setLineToEdit(null)
    executeOrConfirmAction(
      (reason) =>
        patchPoLine(poId, lineId, {
          ...data,
          reason: reason || 'Chỉnh sửa dòng hàng',
        }),
      'Đã cập nhật dòng hàng thành công',
    )
  }

  function handleSaveNewLine(data: {
    material_id: string
    order_qty: number
    order_uom: string
    manual_price?: number
    manual_reason?: string
    thick?: number
    width?: number
    length?: number
  }) {
    setLineAddOpen(false)
    executeOrConfirmAction(
      (reason) =>
        createPoLine(poId, {
          ...data,
          reason: reason || 'Thêm dòng hàng mới',
        }),
      'Đã thêm dòng hàng mới thành công',
    )
  }

  function handleReopen() {
    const reason = window.prompt('Nhập lý do khôi phục đơn hàng:')
    if (reason === null) return
    void handlePoAction(
      () => reopenPurchaseOrder(poId, reason || 'Khôi phục đơn hàng'),
      'Đã khôi phục PO về trạng thái DRAFT',
    )
  }

  async function handleClosePo(closeReason: string) {
    if (!po) return
    setCloseModalLoading(true)
    try {
      for (const line of remainingLines) {
        await recordLineShort(po.id, line.id, {
          short_qty: line.qty - line.receivedQty,
          action: 'cancel_rest',
          note: closeReason || undefined,
        })
      }
      setActionSuccess('Đã đóng PO và hủy phần số lượng còn lại thành công')
      await loadData()
      if (tab === 'history') {
        void loadHistory()
      }
    } catch (err: any) {
      const formatted = formatDocumentError(err)
      setFormattedError(formatted)
      setError(formatted.message)
      throw err
    } finally {
      setCloseModalLoading(false)
    }
  }

  async function handleArchive() {
    if (!po) return
    await handlePoAction(
      () => archivePurchaseOrder(po.id),
      'Đã lưu trữ PO thành công',
    )
  }

  async function handleUnarchive() {
    if (!po) return
    await handlePoAction(
      () => unarchivePurchaseOrder(po.id),
      'Đã bỏ lưu trữ PO thành công',
    )
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Đang tải PO...</div>
  }

  if (!po) {
    return (
      <EmptyState
        title="Không tìm thấy PO"
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/po')}>
            Quay lại
          </Button>
        }
      />
    )
  }

  const supplier = suppliers.find((s) => s.id === po.supplierId)

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'PO', to: '/purchasing/po' },
          { label: po.code },
        ]}
      />
      <PageHeader
        title={po.code}
        subtitle={`${supplier?.name ?? ''} · Ver ${po.version} · Giao ${po.deliveryDate || '—'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isFreePo && (
              <Badge tone="info" className="font-semibold text-xs">
                PO tự do
              </Badge>
            )}
            <PoStatusBadge status={po.status} />
            {deliveryStatus && (
              <StatusBadge
                tone={deliveryStatus.tone}
                label={deliveryStatus.label}
                showDot
              />
            )}
            {isArchived && (
              <Badge tone="neutral" className="font-semibold text-xs">
                Đã lưu trữ
              </Badge>
            )}
            {po.editedAfterApprovalAt && (
              <Badge tone="warning" className="font-semibold text-xs">
                Đã sửa sau duyệt
              </Badge>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/purchasing/po/${po.id}/print`)}
            >
              Xem/In Đơn Hàng
            </Button>
            {!isFreePo && canWritePo && po.status !== 'CANCELLED' && po.status !== 'RECEIVED' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setHeaderEditOpen(true)
                  if (tab === 'lines') {
                    setIsEditingLines(true)
                  }
                }}
              >
                <Edit2 className="mr-1 h-3.5 w-3.5" />
                Sửa PO
              </Button>
            )}
            {isFreePo && canWritePo && po.status === 'DRAFT' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setHeaderEditOpen(true)}
              >
                <Edit2 className="mr-1 h-3.5 w-3.5" />
                Sửa thông tin PO
              </Button>
            )}
            {canWritePo && po.status === 'CANCELLED' && (
              <Button
                size="sm"
                variant="primary"
                onClick={handleReopen}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                Khôi phục PO
              </Button>
            )}
            {po.status === 'DRAFT' ? (
              <Button
                size="sm"
                onClick={() =>
                  handlePoAction(() => submitPurchaseOrder(po.id), 'Đã gửi duyệt PO')
                }
              >
                Gửi duyệt
              </Button>
            ) : null}
            {po.status === 'PENDING_APPROVAL' ? (
              <Button
                size="sm"
                onClick={() =>
                  handlePoAction(
                    () => approvePurchaseOrder(po.id, { reason: 'Director approved' }),
                    'Director đã duyệt PO',
                  )
                }
              >
                Director duyệt
              </Button>
            ) : null}
            {po.status === 'APPROVED' ? (
              <Button
                size="sm"
                onClick={() =>
                  handlePoAction(() => sendPurchaseOrder(po.id), 'Đã gửi PO cho NCC')
                }
              >
                Gửi NCC
              </Button>
            ) : null}
            {(po.status === 'SENT' || po.status === 'PARTIAL') && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleSwitchTab('receive')}
              >
                Nhận hàng
              </Button>
            )}
            {canWritePo && canWriteReceiving && (po.status === 'PARTIAL' || po.status === 'SENT') && hasRemaining && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setCloseModalOpen(true)}
              >
                Đóng PO
              </Button>
            )}
            {canWritePo && isArchived && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleUnarchive}
              >
                <ArchiveRestore className="mr-1 h-3.5 w-3.5" />
                Bỏ lưu trữ
              </Button>
            )}
            {canWritePo && !isArchived && canArchive && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleArchive}
              >
                <Archive className="mr-1 h-3.5 w-3.5" />
                Lưu trữ PO
              </Button>
            )}
            {po.status !== 'CANCELLED' && po.status !== 'RECEIVED' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt('Nhập lý do hủy PO:')
                  if (reason) {
                    void handlePoAction(
                      () => cancelPurchaseOrder(po.id, reason),
                      'Đã hủy PO',
                    )
                  }
                }}
              >
                Hủy PO
              </Button>
            )}
          </div>
        }
      />

      {isArchived && po.archivedAt && (
        <div className="mb-3 rounded-[var(--radius-md)] border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center gap-2">
          <Archive className="h-4 w-4 text-slate-500" />
          <span>
            Đơn hàng đã được lưu trữ bởi <strong>{po.archivedBy || 'người dùng'}</strong> vào{' '}
            <strong>{formatDateFull(po.archivedAt)}</strong>.
          </span>
        </div>
      )}

      {formattedError ? (
        <div className="mb-3 rounded-[var(--radius-md)] border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          <div className="font-semibold mb-0.5">Lỗi ({formattedError.code}):</div>
          <div>{formattedError.message}</div>
          {formattedError.link && (
            <div className="mt-2">
              <a
                href={formattedError.link.url}
                className="font-semibold text-blue-600 underline hover:text-blue-800"
              >
                {formattedError.link.label} →
              </a>
            </div>
          )}
        </div>
      ) : error ? (
        <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {actionSuccess}
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <KpiCard
          label={PO_TOTAL_LABEL}
          value={money(currentTotal)}
        />
        <KpiCard
          label="Tiến độ nhận"
          value={formatReceivePct(poReceiveProgress(po))}
        />
        <KpiCard label="Số dòng" value={isFreePo ? freeRows.length : tableLines.length} />
        <KpiCard
          label="VAT"
          value={isFreePo ? '—' : (poHasUnknownVat(po) ? 'Có UNKNOWN' : 'Đã biết')}
          tone={isFreePo ? 'default' : (poHasUnknownVat(po) ? 'warning' : 'default')}
        />
      </div>

      <PageToolbar
        left={
          <div className="flex rounded-[var(--radius-sm)] border border-[var(--color-border)] p-0.5">
            {(
              isFreePo
                ? ([
                    ['lines', 'Dòng hàng'],
                    ['receive', 'Nhận hàng'],
                    ['history', 'Lịch sử workflow'],
                    ['files', 'File đính kèm'],
                  ] as const)
                : ([
                    ['lines', 'Dòng hàng'],
                    ['receive', 'Nhận hàng'],
                    ['delivery', 'Lịch sử nhận hàng'],
                    ['history', 'Lịch sử workflow'],
                    ['files', 'File đính kèm'],
                  ] as const)
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`rounded-[var(--radius-sm)] px-3 py-1 text-xs font-semibold ${
                  tab === id
                    ? 'bg-[var(--color-primary)] text-white'
                    : 'text-[var(--color-text-muted)] hover:bg-slate-50'
                }`}
                onClick={() => handleSwitchTab(id as any)}
              >
                {label}
              </button>
            ))}
          </div>
        }
        right={
          !isFreePo && tab === 'lines' && canWritePo && po.status !== 'CANCELLED' && po.status !== 'RECEIVED' ? (
            !isEditingLines ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsEditingLines(true)}
              >
                <Edit2 className="mr-1.5 h-3.5 w-3.5" />
                Sửa dòng hàng
              </Button>
            ) : !isLinesDirty ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsEditingLines(false)}
              >
                Thoát chế độ sửa
              </Button>
            ) : null
          ) : null
        }
      />

      {tab === 'lines' ? (
        isFreePo ? (
          <div className="space-y-2">
            {isFreeDirty && (
              <div className="flex items-center justify-between rounded-[var(--radius-md)] border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                <span className="font-medium">
                  Bạn có thay đổi chưa lưu trên bảng PO tự do.
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleCancelFreeContent}
                    disabled={savingFreeContent}
                  >
                    Hủy thay đổi
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleSaveFreeContent}
                    disabled={savingFreeContent}
                  >
                    <Save className="mr-1 h-3.5 w-3.5" />
                    {savingFreeContent ? 'Đang lưu...' : 'Lưu thay đổi'}
                  </Button>
                </div>
              </div>
            )}

            <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-[var(--shadow-level-1)]">
              <FreeSheetTable
                columns={freeColumns}
                rows={freeRows}
                onChange={({ columns: nextCols, rows: nextRows }) => {
                  setFreeColumns(nextCols)
                  setFreeRows(nextRows)
                }}
                readOnly={!canWritePo || po.status !== 'DRAFT'}
                computeRow={computeFreeRow}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {isEditingLines && !isLinesDirty && (
              <div className="flex items-center justify-between rounded-[var(--radius-md)] border border-blue-200 bg-blue-50/80 px-4 py-2 text-xs text-blue-900">
                <span className="font-medium">
                  Đang ở chế độ chỉnh sửa dòng hàng. Bạn có thể sửa trực tiếp trên bảng hoặc thêm dòng mới.
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setIsEditingLines(false)}
                >
                  Thoát chế độ sửa
                </Button>
              </div>
            )}

            {isLinesDirty && (
              <div className="flex items-center justify-between rounded-[var(--radius-md)] border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                <span className="font-medium">
                  Bạn có thay đổi chưa lưu trên bảng dòng hàng.
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleCancelLinesEdit}
                    disabled={savingLines}
                  >
                    Hủy thay đổi
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleSaveLinesClick}
                    disabled={savingLines}
                  >
                    <Save className="mr-1 h-3.5 w-3.5" />
                    {savingLines ? 'Đang lưu...' : 'Lưu thay đổi'}
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
              <ExcelEditableTable<PoLineTableRow>
                columns={lineColumns}
                data={tableLines}
                onChange={handleLinesTableChange}
                emptyRowFactory={emptyRowFactory}
                regenerateId={(row) => ({
                  ...row,
                  id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                  isNew: true,
                  receivedQty: 0,
                  shortQty: 0,
                  shortResolution: null,
                })}
                readOnly={
                  !isEditingLines || !canWritePo || po.status === 'CANCELLED' || po.status === 'RECEIVED'
                }
                addRowLabel="Thêm dòng hàng"
                storageKey={`po-lines-${po.id}`}
                showActions={
                  isEditingLines && canWritePo && po.status !== 'CANCELLED' && po.status !== 'RECEIVED'
                }
              />
            </div>
          </div>
        )
      ) : null}

      {tab === 'receive' ? (
        isFreePo ? (
          <FreePoReceiveTab
            poId={po.id}
            poDto={poDto}
            onUpdated={loadData}
          />
        ) : (
          <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
            {po.status === 'SENT' || po.status === 'PARTIAL' ? (
              <>
                <h3 className="mb-2 text-sm font-semibold">Nhận hàng nhanh</h3>
                {po.lines.map((l) => (
                  <div key={l.id} className="mb-2 flex flex-wrap items-center gap-3 text-sm py-1.5 border-b border-slate-100 last:border-b-0">
                    <div className="min-w-[180px] max-w-xs flex-1">
                      <div className="font-medium text-slate-800">{l.itemName}</div>
                      <div className="text-[11px] font-mono text-slate-500">{l.spec || '—'}</div>
                    </div>
                    <span className="text-slate-500 text-xs">
                      Đã đặt: {formatVnNumber(l.qty)} {l.uom} · Đã nhận: {formatVnNumber(l.receivedQty)} {l.uom} · Còn lại:{' '}
                      <span className="font-semibold text-amber-700">
                        {formatVnNumber(Math.max(0, l.qty - l.receivedQty - l.shortQty))} {l.uom}
                      </span>
                    </span>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={0}
                        className="h-8 w-28 rounded border border-[var(--color-border)] px-2 font-mono text-xs"
                        placeholder="Qty nhận"
                        value={recvQty[l.id] ?? ''}
                        onChange={(e) => setRecvQty({ ...recvQty, [l.id]: e.target.value })}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const rem = Math.max(0, l.qty - l.receivedQty - l.shortQty)
                          setRecvQty({ ...recvQty, [l.id]: String(rem) })
                        }}
                      >
                        Nhận tất cả
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => {
                      const lines = po.lines
                        .map((l) => ({
                          po_line_id: l.id,
                          qty: Number(recvQty[l.id] || 0),
                        }))
                        .filter((l) => l.qty > 0)
                      if (lines.length === 0) {
                        setError('Nhập số lượng nhận cho ít nhất một dòng')
                        return
                      }
                      void handlePoAction(
                        () => receivePurchaseOrder(po.id, { lines }),
                        'Ghi nhận nhận hàng thành công',
                      )
                    }}
                  >
                    Ghi nhận nhận hàng
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setRecvQty({})
                      setTab('lines')
                    }}
                  >
                    Hủy / Quay lại
                  </Button>
                  {po.lines.some((l) => l.receivedQty > 0 && l.receivedQty < l.qty) ? (
                    <>
                      <input
                        className="h-8 w-24 rounded border border-[var(--color-border)] px-2 text-sm"
                        value={shortQty}
                        onChange={(e) => setShortQty(e.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          handlePoAction(
                            () =>
                              recordLineShort(po.id, po.lines[0].id, {
                                short_qty: Number(shortQty) || 0,
                                action: 'wait',
                              }),
                            'Đã ghi nhận: Chờ giao bù sau',
                          )
                        }
                      >
                        Chờ giao bù sau
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          handlePoAction(
                            () =>
                              recordLineShort(po.id, po.lines[0].id, {
                                short_qty: Number(shortQty) || 0,
                                action: 'accept',
                              }),
                            'Đã ghi nhận: Chấp nhận thiếu hàng',
                          )
                        }
                      >
                        Chấp nhận thiếu hàng
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          handlePoAction(
                            () =>
                              recordLineShort(po.id, po.lines[0].id, {
                                short_qty: Number(shortQty) || 0,
                                action: 'cancel_rest',
                              }),
                            'Đã ghi nhận: Hủy phần còn thiếu',
                          )
                        }
                      >
                        Hủy phần còn thiếu
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          handlePoAction(
                            () =>
                              recordLineShort(po.id, po.lines[0].id, {
                                short_qty: Number(shortQty) || 0,
                                action: 'claim',
                              }),
                            'Đã ghi nhận: Yêu cầu NCC bù hàng',
                          )
                        }
                      >
                        Yêu cầu NCC bù hàng
                      </Button>
                    </>
                  ) : null}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">
                Nhận hàng chỉ khả dụng khi PO ở trạng thái SENT hoặc PARTIAL.
              </p>
            )}
          </div>
        )
      ) : null}

      {tab === 'files' ? (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <AttachmentGallery
            entityType="purchase_order"
            entityId={po.id}
            adapter={poAttachmentAdapter}
            readOnly={!canWritePo}
            onFilesChanged={() => setFileExplorerRefreshKey((key) => key + 1)}
          />
          <FileExplorer
            key={fileExplorerRefreshKey}
            productCode={po.id}
            folderSlug="attachments"
            rootLabel="File đính kèm PO (phiếu ký, phiếu giao hàng...)"
            service={poWorkspaceService}
          />
        </div>
      ) : null}

      {tab === 'delivery' ? (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <PoDeliveryHistoryTable poId={po.id} />
        </div>
      ) : null}

      {tab === 'history' ? (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
          <DocumentHistoryTab
            logs={historyLogs}
            loading={historyLoading}
            error={historyError}
            emptyMessage="Chưa có lịch sử thay đổi cho đơn mua hàng này."
          />
          {po.note ? (
            <p className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
              Ghi chú đơn hàng: {po.note}
            </p>
          ) : null}
        </div>
      ) : null}

      <PoHeaderEditModal
        isOpen={headerEditOpen}
        onClose={() => setHeaderEditOpen(false)}
        onSave={handleSaveHeader}
        suppliers={suppliers}
        initialData={{
          supplier_id: po.supplierId,
          delivery_date: po.deliveryDate || null,
          delivery_site: poDto?.delivery_site ?? null,
          payment_terms: poDto?.payment_terms ?? null,
          note: po.note ?? poDto?.note ?? null,
          signers,
        }}
        hasReceipts={hasReceipts}
      />

      <PoLineEditModal
        isOpen={lineEditOpen}
        onClose={() => {
          setLineEditOpen(false)
          setLineToEdit(null)
        }}
        onSave={handleSaveLine}
        line={lineToEdit}
        materials={materials}
      />

      <PoLineAddModal
        isOpen={lineAddOpen}
        onClose={() => setLineAddOpen(false)}
        onSave={handleSaveNewLine}
        materials={materials}
      />

      <ConfirmPostApprovalEditDialog
        isOpen={confirmPostApprovalOpen}
        documentCode={po.code}
        onClose={() => {
          setConfirmPostApprovalOpen(false)
          setPendingAction(null)
        }}
        onConfirm={handleConfirmPostApproval}
      />

      <PoCloseModal
        isOpen={closeModalOpen}
        onClose={() => setCloseModalOpen(false)}
        poCode={po.code}
        remainingLines={remainingLines}
        onConfirm={handleClosePo}
        loading={closeModalLoading}
      />

      <PoLineTraceabilityModal
        isOpen={traceModalOpen}
        onClose={() => setTraceModalOpen(false)}
        data={traceData}
        loading={traceLoading}
        error={traceError}
      />
    </div>
  )
}
